import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Home, Users, Calendar, Newspaper, Trophy, Code, BookOpen,
  MessageSquare, Settings, User, Zap, LogOut, Moon, Sun, Gamepad2,
  Award, FolderKanban, HelpCircle, X
} from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { useThemeStore } from '../stores/themeStore';

interface Command {
  id: string;
  label: string;
  icon: React.ReactNode;
  action: () => void;
  keywords?: string[];
  category: 'navigation' | 'actions' | 'user';
}

export function CommandPalette() {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { user, profile, signOut } = useAuthStore();
  const { theme, toggleTheme } = useThemeStore();

  // Define all commands
  const commands: Command[] = [
    // Navigation
    { id: 'home', label: 'Go to Home', icon: <Home size={18} />, action: () => navigate('/'), keywords: ['home', 'main', 'landing'], category: 'navigation' },
    { id: 'feed', label: 'Go to Feed', icon: <MessageSquare size={18} />, action: () => navigate('/feed'), keywords: ['feed', 'posts', 'social'], category: 'navigation' },
    { id: 'events', label: 'Go to Events', icon: <Calendar size={18} />, action: () => navigate('/events'), keywords: ['events', 'calendar', 'meetups'], category: 'navigation' },
    { id: 'hackathons', label: 'Go to Hackathons', icon: <Zap size={18} />, action: () => navigate('/hackathons'), keywords: ['hackathons', 'competition', 'coding'], category: 'navigation' },
    { id: 'projects', label: 'Go to Projects', icon: <FolderKanban size={18} />, action: () => navigate('/projects'), keywords: ['projects', 'showcase', 'portfolio'], category: 'navigation' },
    { id: 'news', label: 'Go to News', icon: <Newspaper size={18} />, action: () => navigate('/news'), keywords: ['news', 'blog', 'articles'], category: 'navigation' },
    { id: 'leaderboard', label: 'Go to Leaderboard', icon: <Trophy size={18} />, action: () => navigate('/leaderboard'), keywords: ['leaderboard', 'ranking', 'points'], category: 'navigation' },
    { id: 'members', label: 'Go to Members', icon: <Users size={18} />, action: () => navigate('/members'), keywords: ['members', 'people', 'community'], category: 'navigation' },
    { id: 'fun', label: 'Go to Fun Zone', icon: <Gamepad2 size={18} />, action: () => navigate('/fun'), keywords: ['fun', 'games', 'wordle', 'meme'], category: 'navigation' },
    { id: 'challenges', label: 'Go to Challenges', icon: <Code size={18} />, action: () => navigate('/challenges'), keywords: ['challenges', 'coding', 'practice'], category: 'navigation' },
    { id: 'team-builder', label: 'Find Teammates', icon: <Users size={18} />, action: () => navigate('/team-builder'), keywords: ['team', 'hackathon', 'teammates', 'find'], category: 'navigation' },
    { id: 'feature-requests', label: 'Feature Requests', icon: <Zap size={18} />, action: () => navigate('/feature-requests'), keywords: ['feature', 'request', 'suggest', 'vote'], category: 'navigation' },
    { id: 'saved', label: 'Reading List', icon: <BookOpen size={18} />, action: () => navigate('/saved'), keywords: ['saved', 'reading', 'bookmarks', 'later'], category: 'navigation' },
    { id: 'help', label: 'Help & Guide', icon: <HelpCircle size={18} />, action: () => navigate('/help'), keywords: ['help', 'guide', 'tutorial', 'how to', 'faq'], category: 'navigation' },
    
    // User actions
    ...(user ? [
      { id: 'profile', label: 'View My Profile', icon: <User size={18} />, action: () => navigate(`/profile/${profile?.username}`), keywords: ['profile', 'my', 'account'], category: 'user' as const },
      { id: 'settings', label: 'Open Settings', icon: <Settings size={18} />, action: () => navigate('/settings'), keywords: ['settings', 'preferences', 'config'], category: 'user' as const },
      { id: 'messages', label: 'Open Messages', icon: <MessageSquare size={18} />, action: () => navigate('/messages'), keywords: ['messages', 'dm', 'chat'], category: 'user' as const },
      { id: 'achievements', label: 'View Achievements', icon: <Award size={18} />, action: () => navigate(`/profile/${profile?.username}#achievements`), keywords: ['achievements', 'badges'], category: 'user' as const },
      { id: 'logout', label: 'Sign Out', icon: <LogOut size={18} />, action: () => signOut(), keywords: ['logout', 'signout', 'exit'], category: 'user' as const },
    ] : []),
    
    // Actions
    { id: 'theme', label: theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode', icon: theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />, action: () => toggleTheme(), keywords: ['theme', 'dark', 'light', 'mode'], category: 'actions' },
    { id: 'help', label: 'Get Help', icon: <HelpCircle size={18} />, action: () => window.open('https://t.me/naol_mid', '_blank'), keywords: ['help', 'support', 'contact'], category: 'actions' },
  ];

  // Filter commands based on query
  const filteredCommands = query
    ? commands.filter(cmd => {
        const searchText = `${cmd.label} ${cmd.keywords?.join(' ') || ''}`.toLowerCase();
        return searchText.includes(query.toLowerCase());
      })
    : commands;

  // Group by category
  const groupedCommands = {
    navigation: filteredCommands.filter(c => c.category === 'navigation'),
    user: filteredCommands.filter(c => c.category === 'user'),
    actions: filteredCommands.filter(c => c.category === 'actions'),
  };

  const allFiltered = [...groupedCommands.navigation, ...groupedCommands.user, ...groupedCommands.actions];

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Open with Cmd+K or Ctrl+K
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }
      
      // Close with Escape
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
      setQuery('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, allFiltered.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (allFiltered[selectedIndex]) {
          allFiltered[selectedIndex].action();
          setIsOpen(false);
        }
        break;
    }
  }, [allFiltered, selectedIndex]);

  // Reset selection when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={() => setIsOpen(false)}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-xl mx-4 bg-slate-900 rounded-2xl border border-slate-700 shadow-2xl overflow-hidden">
        {/* Search Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-800">
          <Search className="w-5 h-5 text-slate-400" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search commands..."
            className="flex-1 bg-transparent text-white placeholder:text-slate-500 focus:outline-none text-lg"
          />
          <div className="flex items-center gap-1">
            <kbd className="px-2 py-0.5 text-xs bg-slate-800 text-slate-400 rounded">ESC</kbd>
          </div>
          <button onClick={() => setIsOpen(false)} className="text-slate-400 hover:text-white">
            <X size={18} />
          </button>
        </div>

        {/* Results */}
        <div className="max-h-80 overflow-y-auto p-2">
          {allFiltered.length === 0 ? (
            <div className="py-8 text-center text-slate-500">
              <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No results found</p>
            </div>
          ) : (
            <>
              {groupedCommands.navigation.length > 0 && (
                <div className="mb-2">
                  <p className="px-3 py-1 text-xs font-medium text-slate-500 uppercase">Navigation</p>
                  {groupedCommands.navigation.map((cmd, i) => {
                    const globalIndex = i;
                    return (
                      <button
                        key={cmd.id}
                        onClick={() => { cmd.action(); setIsOpen(false); }}
                        onMouseEnter={() => setSelectedIndex(globalIndex)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${
                          selectedIndex === globalIndex
                            ? 'bg-green-500/20 text-green-400'
                            : 'text-slate-300 hover:bg-slate-800'
                        }`}
                      >
                        <span className={selectedIndex === globalIndex ? 'text-green-400' : 'text-slate-500'}>
                          {cmd.icon}
                        </span>
                        <span className="font-medium">{cmd.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {groupedCommands.user.length > 0 && (
                <div className="mb-2">
                  <p className="px-3 py-1 text-xs font-medium text-slate-500 uppercase">Account</p>
                  {groupedCommands.user.map((cmd, i) => {
                    const globalIndex = groupedCommands.navigation.length + i;
                    return (
                      <button
                        key={cmd.id}
                        onClick={() => { cmd.action(); setIsOpen(false); }}
                        onMouseEnter={() => setSelectedIndex(globalIndex)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${
                          selectedIndex === globalIndex
                            ? 'bg-green-500/20 text-green-400'
                            : 'text-slate-300 hover:bg-slate-800'
                        }`}
                      >
                        <span className={selectedIndex === globalIndex ? 'text-green-400' : 'text-slate-500'}>
                          {cmd.icon}
                        </span>
                        <span className="font-medium">{cmd.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {groupedCommands.actions.length > 0 && (
                <div>
                  <p className="px-3 py-1 text-xs font-medium text-slate-500 uppercase">Actions</p>
                  {groupedCommands.actions.map((cmd, i) => {
                    const globalIndex = groupedCommands.navigation.length + groupedCommands.user.length + i;
                    return (
                      <button
                        key={cmd.id}
                        onClick={() => { cmd.action(); setIsOpen(false); }}
                        onMouseEnter={() => setSelectedIndex(globalIndex)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${
                          selectedIndex === globalIndex
                            ? 'bg-green-500/20 text-green-400'
                            : 'text-slate-300 hover:bg-slate-800'
                        }`}
                      >
                        <span className={selectedIndex === globalIndex ? 'text-green-400' : 'text-slate-500'}>
                          {cmd.icon}
                        </span>
                        <span className="font-medium">{cmd.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-slate-800 flex items-center justify-between text-xs text-slate-500">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-slate-800 rounded">↑↓</kbd>
              navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-slate-800 rounded">↵</kbd>
              select
            </span>
          </div>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-slate-800 rounded">⌘K</kbd>
            to open
          </span>
        </div>
      </div>
    </div>
  );
}

export default CommandPalette;

